import { FetchResult } from 'src/types/TApiQueries';
import {
  Share,
  ShareResponse,
  SharesListResponse,
  CreateShareRequest,
  CreateShareResponse,
  UpdateShareRequest,
  SharesListParams,
  ShareCommentsResponse,
  ShareApprovalsResponse,
  ShareAnalyticsResponse,
} from 'src/types/TShare';
import { config } from 'src/utils/config';
import { getStandardHeaders, getQueryString, performFetchWithLogging } from '../utils/helpers';

/**
 * List/Search shares for the authenticated user
 * POST /api/v1/assetshares
 * 
 * Search params:
 * - keyword: search by title
 * - start_date, end_date: expiry date range (YYYY-MM-DD)
 * - shared_with: email address
 * - status: OK, EXPIRED, DISABLED
 * - approval_summary: has_pending, all_approved, has_rejected
 * - sort_by: array e.g. ["expiry_date DESC"]
 * - offset, limit: pagination
 */
export async function listShares(
  accessToken: string,
  params?: SharesListParams,
): Promise<FetchResult<SharesListResponse>> {
  const url = `${config.hostUrl}/api/v1/assetshares`;

  // Build clean request body - only include defined values
  const body: Record<string, any> = {};
  
  if (params) {
    if (params.keyword) body.keyword = params.keyword;
    if (params.start_date) body.start_date = params.start_date;
    if (params.end_date) body.end_date = params.end_date;
    if (params.shared_with) body.shared_with = params.shared_with;
    if (params.status) body.status = params.status;
    if (params.approval_summary) body.approval_summary = params.approval_summary;
    if (params.sort_by && params.sort_by.length > 0) body.sort_by = params.sort_by;
    if (typeof params.offset === 'number') body.offset = params.offset;
    if (typeof params.limit === 'number') body.limit = params.limit;
  }

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(body),
  });
}

/**
 * Lightweight count-only response. Backend honours `count_only=y` by
 * skipping the per-row hydration and returning just the total — used
 * by the /usage dashboard tile so the page doesn't pay for a full
 * share listing on every visit.
 */
export type ShareCountResponse = {
  status: 'OK' | 'ERROR';
  data: {
    shares_found: number;
    shares: never[];
    baseURL?: string;
  };
};

export async function getShareCount(
  accessToken: string,
): Promise<FetchResult<ShareCountResponse>> {
  const url = `${config.hostUrl}/api/v1/assetshares?count_only=y`;

  return performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

/**
 * Get a single share by ID (public endpoint - no auth required unless password protected)
 * GET /api/v1/assetshare/{shareId}
 */
export async function getShare(shareId: string): Promise<FetchResult<ShareResponse>> {
  const url = `${config.hostUrl}/api/v1/assetshare/${shareId}`;

  return performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(),
    credentials: 'include',
  });
}

/**
 * Get a single share by ID (authenticated - for owner)
 * GET /api/v1/assetshare/{shareId}
 */
export async function getShareAuthenticated(
  accessToken: string,
  shareId: string,
): Promise<FetchResult<ShareResponse>> {
  const url = `${config.hostUrl}/api/v1/assetshare/${shareId}`;

  return performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

/**
 * Create a new share
 * PUT /api/v1/assetshare
 */
export async function createShare(
  accessToken: string,
  data: CreateShareRequest,
): Promise<FetchResult<CreateShareResponse>> {
  const url = `${config.hostUrl}/api/v1/assetshare`;
  const payload = JSON.stringify(data);

  return performFetchWithLogging(url, {
    method: 'PUT',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: payload,
  });
}

/**
 * Update an existing share (patch)
 * POST /api/v1/assetshare/{shareId}?op=patch
 */
export async function updateShare(
  accessToken: string,
  shareId: string,
  data: UpdateShareRequest,
): Promise<FetchResult<ShareResponse>> {
  const url = `${config.hostUrl}/api/v1/assetshare/${shareId}?op=patch`;
  const payload = JSON.stringify(data);

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: payload,
  });
}

/**
 * Delete a share
 * Note: The API may not have a dedicated delete endpoint - this may need adjustment
 * based on actual API behavior (might use updateShare with status change)
 */
export async function deleteShare(
  accessToken: string,
  shareId: string,
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assetshare/${shareId}`;

  return performFetchWithLogging(url, {
    method: 'DELETE',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

/**
 * Access a password-protected share (public endpoint)
 * POST /api/v1/assetshare/{shareId}
 */
export async function accessShareWithPassword(
  shareId: string,
  password: string,
): Promise<FetchResult<ShareResponse>> {
  const url = `${config.hostUrl}/api/v1/assetshare/${shareId}`;
  const payload = JSON.stringify({ password });

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(),
    credentials: 'include',
    body: payload,
  });
}

// ============================================
// Comments API
// ============================================

/**
 * Get comments for a share (authenticated - owner only)
 * GET /api/v1/assetshares/{shareId}/comments
 */
export async function getShareComments(
  accessToken: string,
  shareId: string,
): Promise<FetchResult<ShareCommentsResponse>> {
  const url = `${config.hostUrl}/api/v1/assetshares/${shareId}/comments`;

  return performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

/**
 * Delete a comment from a share (authenticated - owner only)
 * DELETE /api/v1/assetshares/{shareId}/comments/{commentId}
 */
export async function deleteShareComment(
  accessToken: string,
  shareId: string,
  commentId: number,
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assetshares/${shareId}/comments/${commentId}`;

  return performFetchWithLogging(url, {
    method: 'DELETE',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

// ============================================
// Approvals API
// ============================================

/**
 * Get approval statuses for a share (authenticated - owner only)
 * GET /api/v1/assetshares/{shareId}/approvals
 */
export async function getShareApprovals(
  accessToken: string,
  shareId: string,
): Promise<FetchResult<ShareApprovalsResponse>> {
  const url = `${config.hostUrl}/api/v1/assetshares/${shareId}/approvals`;

  return performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

// ============================================
// Analytics API
// ============================================

export type AnalyticsParams = {
  start_date?: string;
  end_date?: string;
};

/**
 * Get analytics for a share (authenticated - owner only)
 * GET /api/v1/assetshares/{shareId}/analytics
 */
export async function getShareAnalytics(
  accessToken: string,
  shareId: string,
  params?: AnalyticsParams,
): Promise<FetchResult<ShareAnalyticsResponse>> {
  let url = `${config.hostUrl}/api/v1/assetshares/${shareId}/analytics`;
  
  if (params) {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    const queryString = queryParams.toString();
    if (queryString) url += `?${queryString}`;
  }

  return performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

// ============================================
// Public Share APIs (no auth required)
// ============================================

export type PublicCommentRequest = {
  commenter_name?: string;
  commenter_email?: string;
  comment: string;
  asset_id?: string;
};

export type PublicApprovalRequest = {
  status: 'APPROVED' | 'REJECTED';
  reviewer_name?: string;
  reviewer_email?: string;
  notes?: string;
};

export type BulkApprovalAsset = {
  asset_id: string;
  status: 'APPROVED' | 'REJECTED';
  notes?: string;
};

export type BulkApprovalRequest = {
  assets: BulkApprovalAsset[];
  reviewer_name: string;
  reviewer_email: string;
};

/**
 * Add a comment to a share (public endpoint)
 * POST /api/v1/assetshare/{shareId}/comment
 */
export async function addPublicComment(
  shareId: string,
  data: PublicCommentRequest,
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assetshare/${shareId}/comment`;
  const payload = JSON.stringify(data);

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(),
    credentials: 'include',
    body: payload,
  });
}

/**
 * Approve or reject an asset in a share (public endpoint)
 * POST /api/v1/assetshare/{shareId}/assets/{assetId}/approve
 */
export async function approveAsset(
  shareId: string,
  assetId: string,
  data: PublicApprovalRequest,
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assetshare/${shareId}/assets/${assetId}/approve`;
  const payload = JSON.stringify(data);

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(),
    credentials: 'include',
    body: payload,
  });
}

/**
 * Bulk approve or reject multiple assets in a share (public endpoint)
 * POST /api/v1/assetshare/{shareId}/approvals/bulk
 */
export async function bulkApproveAssets(
  shareId: string,
  data: BulkApprovalRequest,
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assetshare/${shareId}/approvals/bulk`;
  const payload = JSON.stringify(data);

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(),
    credentials: 'include',
    body: payload,
  });
}

export type TrackDownloadRequest = {
  asset_id: string;
  download_key: string;
};

/**
 * Track a download event for analytics (public endpoint)
 * POST /api/v1/assetshares/{shareId}/analytics
 */
export async function trackShareDownload(
  shareId: string,
  data: TrackDownloadRequest,
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assetshares/${shareId}/analytics`;
  const payload = JSON.stringify(data);

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(),
    credentials: 'include',
    body: payload,
  });
}

